import React, { useEffect, useState } from 'react';
import { apiRequest } from '../utils/api';
import { toast } from 'react-toastify';
import { FileText, BarChart3, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUsage } from '../contexts/UsageContext';
import SubscriptionNotification from '../components/shared/SubscriptionNotification';
import ProcessingDisclaimerModal, { useProcessingDisclaimer } from '../components/shared/ProcessingDisclaimerModal';
import formatDocumentDisplayName from '../utils/documentDisplayName';
import { useAuth } from '../components/auth/AuthComponents';
import { canGenerateOrRevampContract } from '../utils/userPermissions';
import { COUNTRY_OPTIONS } from '../constants/countries';
import { PROGRAM_TYPE_OPTIONS, PURPOSE_OPTIONS } from '../constants/formOptions';
import FormSelect from '../components/shared/FormSelect';
import RichTextEditor from '../components/shared/RichTextEditor';
import ConfirmModal from '../components/shared/ConfirmModal';
import { htmlToPlainText, richTextPlainLength } from '../utils/richText';

const defaultGuideStatements = {
  cv: 'Enhance action verbs and quantify achievements with metrics, improve keyword alignment with the target job role, optimize section structure and formatting, and strengthen skills presentation to increase ATS compatibility and recruiter appeal.',
  sop: 'Strengthen the narrative flow, improve the achievements, and better connect goals with the program\'s objectives.',
  contract: 'Focus on clarifying ambiguous terms, simplifying complex clauses, ensuring balanced risk allocation between parties, and improving enforceability while maintaining legal precision.'
};

const styleOptions = [
  'Formal',
  'Professional',
  'Concise',
  'Impactful',
  'Friendly',
  'Persuasive',
  'Conversational',
  'Academic',
  'Clear',
  'Direct',
];

const favourOptions = [
  { value: 'offeror', label: 'Offeror' },
  { value: 'offeree', label: 'Offeree' },
];

// Dynamically set wizard steps based on docType
function getWizardSteps(docType) {
  if (docType === 'cv') {
    return [
      'Type & Purpose',
      'Document & Style',
      'Guide Statement',
      'Review & Submit',
    ];
  }
  if (docType === 'sop') {
    return [
      'Type & Style',
      'Select Document',
      'Program Details',
      'Guide Statement',
      'Review & Submit',
    ];
  }
  return [
    'Type & Style',
    'Document & Guide',
    'Review & Submit',
  ];
}

function DocumentSearchField({
  docSearchInput,
  setDocSearchInput,
  docDropdownOpen,
  setDocDropdownOpen,
  filteredDocs,
  selectedDocId,
  setSelectedDocId,
  docType,
}) {
  return (
    <div className="flex flex-col gap-3">
      <label className="text-[var(--color-text-secondary)] font-medium">Select Document</label>
      <input
        type="text"
        className="themed-input rounded-lg px-4 py-3 focus:ring-2 focus:ring-[var(--color-ring)]"
        placeholder="Type to search..."
        value={docSearchInput}
        onChange={(e) => { setDocSearchInput(e.target.value); setDocDropdownOpen(true); }}
        onFocus={() => setDocDropdownOpen(true)}
        autoComplete="off"
      />
      <div className="min-h-[8rem] max-h-64 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] shadow-inner">
        {filteredDocs.length === 0 ? (
          <p className="px-4 py-10 text-sm text-[var(--color-text-muted)] text-center leading-relaxed">
            {docSearchInput.trim()
              ? 'No matching documents found.'
              : `No ${docType.toUpperCase()} documents found. Analyze a document first.`}
          </p>
        ) : (
          filteredDocs.map((doc) => (
            <div
              key={doc.id}
              className={`px-4 py-3 cursor-pointer border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-primary-soft)] text-[var(--color-text)] transition-colors ${selectedDocId === doc.id ? 'bg-[var(--color-primary-soft)] ring-1 ring-inset ring-[var(--color-primary)]/50' : ''}`}
              onClick={() => {
                setSelectedDocId(doc.id);
                setDocSearchInput(formatDocumentDisplayName(doc.original_filename || doc.filename, {
                  documentType: doc.document_type,
                  documentId: doc.id,
                }));
                setDocDropdownOpen(true);
              }}
            >
              {formatDocumentDisplayName(doc.original_filename || doc.filename, {
                documentType: doc.document_type,
                documentId: doc.id,
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const RevampMenuPage = () => {
  const { refreshUsage } = useUsage();
  const { user } = useAuth();
  const canRevampContract = canGenerateOrRevampContract(user?.role);
  const [revamps, setRevamps] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [docType, setDocType] = useState('cv');
  const [selectedDocId, setSelectedDocId] = useState('');
  const [search, setSearch] = useState('');
  const [guideStatement, setGuideStatement] = useState('');
  const [style, setStyle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalDocs, setModalDocs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [docTypeTouched, setDocTypeTouched] = useState(false);
  const [docSearchInput, setDocSearchInput] = useState('');
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [docDropdownOpen, setDocDropdownOpen] = useState(false);
  const [inFavourOf, setInFavourOf] = useState('');
  const [programType, setProgramType] = useState('');
  const [university, setUniversity] = useState('');
  const [country, setCountry] = useState('');
  const [courseTitle, setCourseTitle] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [preferredFullLegalName, setPreferredFullLegalName] = useState('');
  const [revampToDelete, setRevampToDelete] = useState(null);

  useEffect(() => {
    if (!canRevampContract && docType === 'contract') {
      setDocType('cv');
    }
  }, [canRevampContract, docType]);

  useEffect(() => {
    if (user?.full_name && !preferredFullLegalName) {
      setPreferredFullLegalName(user.full_name);
    }
  }, [user?.full_name, preferredFullLegalName]);

  useEffect(() => {
    setSelectedDocId('');
    setDocSearchInput('');
    setProgramType('');
    setUniversity('');
    setCountry('');
    setCourseTitle('');
    setCourseDescription('');
    setPreferredFullLegalName(user?.full_name || '');
    setWizardStep(0);
  }, [docType, user?.full_name]);
  const navigate = useNavigate();
  const {
    open: disclaimerOpen,
    requestProcessing,
    confirmProcessing,
    dismissDisclaimer,
  } = useProcessingDisclaimer();

  const handleRevampSubmit = (e) => {
    e.preventDefault();
    requestProcessing(() => handleRevamp(e));
  };

  useEffect(() => {
    // Fetch all user's revamped documents (could be paginated)
    const fetchRevamps = async () => {
      setLoading(true);
      try {
        // Fetch all user documents for modal dropdown
        const docs = await apiRequest('documents/history');
        setDocuments(docs.documents || []);
        // Fetch all user revamps for main list
        const revampsResp = await apiRequest('documents/revamps');
        setRevamps(revampsResp.revamps || []);
        // Map revamps to display info by joining with documents
        const docMap = {};
        (docs.documents || []).forEach(doc => { docMap[doc.id] = doc; });
        setModalDocs((revampsResp.revamps || []).map(r => {
          const doc = docMap[r.document_id] || {};
          return {
            revampId: r.id,
            id: r.document_id,
            original_filename: doc.original_filename || doc.filename || '',
            filename: doc.filename || '',
            document_type: r.type || doc.document_type || '',
            created_at: r.created_at || doc.created_at,
          };
        }));
      } catch (err) {
        toast.error('Failed to fetch documents.');
      } finally {
        setLoading(false);
      }
    };
    fetchRevamps();
  }, []);

  // Filter documents by type and search for the typeahead
  useEffect(() => {
    const filtered = documents.filter(doc => {
      const displayName = formatDocumentDisplayName(doc.original_filename || doc.filename, {
        documentType: doc.document_type,
        documentId: doc.id,
      });
      const q = docSearchInput.toLowerCase();
      return (doc.document_type || '').toLowerCase() === docType &&
        (displayName.toLowerCase().includes(q) ||
          (doc.original_filename || doc.filename || '').toLowerCase().includes(q));
    });
    setFilteredDocs(filtered);
  }, [docType, docSearchInput, documents]);

  // When document type or selected document changes, autofill guide statement
  useEffect(() => {
    setGuideStatement('');
    setAnalysis(null);
    if (!selectedDocId) {
      setGuideStatement(defaultGuideStatements[docType] || '');
      if (docType !== 'sop') {
        setProgramType('');
        setUniversity('');
        setCountry('');
        setCourseTitle('');
        setCourseDescription('');
      }
      return;
    }
    const doc = documents.find(d => d.id === selectedDocId);
    if (!doc) {
      setGuideStatement(defaultGuideStatements[docType] || '');
      return;
    }
    const analysisResult = doc.analysis_result || doc.analysis || {};
    setAnalysis(analysisResult);
    let gs = defaultGuideStatements[docType] || '';
    if (Array.isArray(analysisResult.suggestions) && analysisResult.suggestions.length > 0) {
      gs += '\nSuggestions: ' + analysisResult.suggestions.join('; ');
    }
    if (Array.isArray(analysisResult.recommendations) && analysisResult.recommendations.length > 0) {
      gs += '\nRecommendations: ' + analysisResult.recommendations.join('; ');
    }
    setGuideStatement(gs);

    if (docType === 'sop' && analysisResult) {
      const snap = analysisResult.program_snapshot && typeof analysisResult.program_snapshot === 'object'
        ? analysisResult.program_snapshot
        : analysisResult;
      setProgramType(String(snap.program_type ?? snap.programType ?? ''));
      setUniversity(String(snap.institution ?? snap.university ?? ''));
      setCountry(String(snap.country ?? ''));
      setCourseTitle(String(snap.course_of_study ?? snap.course_title ?? ''));
      setCourseDescription(String(snap.course_description ?? ''));
    }
  }, [docType, selectedDocId, documents]);

  const handleDeleteRevamp = (revampItem) => {
    setRevampToDelete(revampItem);
  };

  const confirmDeleteRevamp = async () => {
    if (!revampToDelete) return;
    const revampItem = revampToDelete;
    try {
      await apiRequest(`documents/revamps/${revampItem.revampId}`, {
        method: 'DELETE',
      });
      setModalDocs((prev) => prev.filter((item) => item.revampId !== revampItem.revampId));
      setRevamps((prev) => prev.filter((item) => item.id !== revampItem.revampId));
      toast.success('Revamp deleted.');
    } catch (err) {
      toast.error('Failed to delete revamp.');
    } finally {
      setRevampToDelete(null);
    }
  };

  const handleRevamp = async (e) => {
    e.preventDefault();
    if (!selectedDocId) return toast.error('Please select a document.');
    setLoading(true);
    try {
      let body = {};
      if (docType === 'cv') {
        body = {
          purpose: purpose || '',
          guide_statement: guideStatement || '',
          style: style || '',
        };
      } else if (docType === 'sop') {
        body = {
          purpose: '',
          guide_statement: guideStatement || '',
          style: style || '',
          program_type: programType || '',
          university: university || '',
          country: country || '',
          course_title: courseTitle || '',
          course_description: htmlToPlainText(courseDescription || ''),
          preferred_full_legal_name: preferredFullLegalName.trim(),
        };
      } else if (docType === 'contract') {
        body = {
          purpose: '',
          guide_statement: guideStatement || '',
          style: style || '',
          in_favour_of: inFavourOf || '',
        };
      }
      await apiRequest(`documents/${selectedDocId}/revamp`, {
        method: 'POST',
        body,
      });
      toast.success('Revamp requested!');
      // Refresh usage data after successful revamp
      await refreshUsage();
      setShowModal(false);
      setWizardStep(0);
      setSelectedDocId('');
      setDocSearchInput('');
      setPurpose('');
      setGuideStatement('');
      setStyle('');
      setInFavourOf('');
      setProgramType('');
      setUniversity('');
      setCountry('');
      setCourseTitle('');
      setCourseDescription('');
      setPreferredFullLegalName(user?.full_name || '');
      // Refresh revamps/documents list
      const docs = await apiRequest('documents/history');
      setDocuments(docs.documents || []);
      const revampsResp = await apiRequest('documents/revamps');
      setRevamps(revampsResp.revamps || []);
      // Redirect to revamp result page
      navigate(`/revamp-result/${selectedDocId}`);
    } catch (err) {
      toast.error('Failed to revamp document.');
    } finally {
      setLoading(false);
    }
  };

  // Dynamically get steps for current docType
  const wizardSteps = getWizardSteps(docType);

  // Wizard step validation
  const canNext = () => {
    if (docType === 'cv') {
      if (wizardStep === 0) return !!docType && !!purpose;
      if (wizardStep === 1) return !!selectedDocId && !!style;
      if (wizardStep === 2) return true; // Guide statement is optional for CV
      if (wizardStep === 3) return true;
    } else if (docType === 'sop') {
      if (wizardStep === 0) return !!docType && !!style;
      if (wizardStep === 1) return !!selectedDocId;
      if (wizardStep === 2) {
        return preferredFullLegalName.trim().split(/\s+/).length >= 2
          && !!programType.trim() && !!university.trim() && !!country.trim()
          && !!courseTitle.trim() && richTextPlainLength(courseDescription) > 0;
      }
      if (wizardStep === 3) return !!guideStatement.trim();
      if (wizardStep === 4) return true;
    } else {
      // Contract
      if (wizardStep === 0) return !!docType && !!style && !!inFavourOf;
      if (wizardStep === 1) return !!selectedDocId && !!guideStatement.trim();
      if (wizardStep === 2) return true;
    }
    return false;
  };

  // Wizard step content
  const renderWizardStep = () => {
    if (docType === 'cv') {
      switch (wizardStep) {
        case 0:
          return (
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Document Type</label>
                <select
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={docType}
                  onChange={e => { setDocType(e.target.value); setSelectedDocId(''); setDocTypeTouched(true); setDocSearchInput(''); setWizardStep(0); }}
                  required
                >
                  <option value="cv">CV</option>
                  <option value="sop">SOP</option>
                  {canRevampContract ? <option value="contract">Contract</option> : null}
                </select>
              </div>
              <div className="flex-1 flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Purpose</label>
                <FormSelect
                  value={purpose}
                  onChange={setPurpose}
                  options={PURPOSE_OPTIONS}
                  placeholder="Select purpose"
                />
              </div>
            </div>
          );
        case 1:
          return (
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 min-w-0">
                <DocumentSearchField
                  docSearchInput={docSearchInput}
                  setDocSearchInput={setDocSearchInput}
                  docDropdownOpen={docDropdownOpen}
                  setDocDropdownOpen={setDocDropdownOpen}
                  filteredDocs={filteredDocs}
                  selectedDocId={selectedDocId}
                  setSelectedDocId={setSelectedDocId}
                  docType={docType}
                />
              </div>
              <div className="flex-1 flex flex-col lg:max-w-sm">
                <label className="text-[var(--color-text-secondary)] font-medium mb-2">Style/Tone (optional)</label>
                <select
                  className="themed-input rounded-lg px-4 py-3 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={style}
                  onChange={e => setStyle(e.target.value)}
                >
                  <option value="">Select style/tone</option>
                  {styleOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
          );
        case 2:
          return (
            <div className="flex flex-col gap-4">
              <label className="text-[var(--color-text-secondary)] font-medium">Guide Statement (optional)</label>
              <textarea
                className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                value={guideStatement}
                onChange={e => setGuideStatement(e.target.value)}
                rows={4}
              />
            </div>
          );
        case 3:
          // Review & Submit
          const doc = documents.find(d => d.id === selectedDocId);
          return (
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">Review Your Revamp Request</h3>
              <div className={`${window.innerWidth < 1024 ? 'bg-[var(--color-surface-muted)] rounded-lg p-2 text-xs max-h-48 break-words whitespace-pre-line overflow-y-auto text-[var(--color-text-secondary)]' : 'bg-[var(--color-surface-muted)] rounded-lg p-4 text-sm max-h-96 overflow-y-auto text-[var(--color-text-secondary)]'}`}>
                <div><b>Document Type:</b> {docType.toUpperCase()}</div>
                <div><b>Document:</b> {doc ? formatDocumentDisplayName(doc.original_filename || doc.filename, { documentType: doc.document_type, documentId: doc.id }) : ''}</div>
                <div><b>Purpose:</b> {purpose}</div>
                <div><b>Guide Statement:</b> <span>{guideStatement}</span></div>
                <div><b>Style/Tone:</b> {style}</div>
              </div>
              <button
                type="submit"
                className="brand-gradient-btn w-full px-6 py-2 rounded-lg disabled:opacity-60 mt-3"
                disabled={loading}
              >
                {loading ? 'Revamping...' : 'Revamp Now'}
              </button>
            </div>
          );
        default:
          return null;
      }
    }

    if (docType === 'sop') {
      switch (wizardStep) {
        case 0:
          return (
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Document Type</label>
                <select
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={docType}
                  onChange={e => { setDocType(e.target.value); setSelectedDocId(''); setDocTypeTouched(true); setDocSearchInput(''); setWizardStep(0); }}
                  required
                >
                  <option value="cv">CV</option>
                  <option value="sop">SOP</option>
                  {canRevampContract ? <option value="contract">Contract</option> : null}
                </select>
              </div>
              <div className="flex-1 flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Style/Tone</label>
                <select
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={style}
                  onChange={e => setStyle(e.target.value)}
                  required
                >
                  <option value="">Select style/tone</option>
                  {styleOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
          );
        case 1:
          return (
            <DocumentSearchField
              docSearchInput={docSearchInput}
              setDocSearchInput={setDocSearchInput}
              docDropdownOpen={docDropdownOpen}
              setDocDropdownOpen={setDocDropdownOpen}
              filteredDocs={filteredDocs}
              selectedDocId={selectedDocId}
              setSelectedDocId={setSelectedDocId}
              docType={docType}
            />
          );
        case 2:
          return (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-[var(--color-text-muted)]">Program details help tailor the revamp to your target course.</p>
              <div className="flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Preferred Full Legal Name</label>
                <input
                  type="text"
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={preferredFullLegalName}
                  onChange={e => setPreferredFullLegalName(e.target.value)}
                  placeholder="e.g., Victor Babatunde Adeleye"
                  required
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">Used on the SOP cover line under &quot;BY&quot;. Overrides any name found in the original document.</p>
              </div>
              <div className="flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Program Type</label>
                <FormSelect
                  value={programType}
                  onChange={setProgramType}
                  options={PROGRAM_TYPE_OPTIONS}
                  placeholder="Select program type"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">University</label>
                <input
                  type="text"
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={university}
                  onChange={e => setUniversity(e.target.value)}
                  placeholder="Target university"
                  required
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Country</label>
                <FormSelect
                  value={country}
                  onChange={setCountry}
                  options={COUNTRY_OPTIONS}
                  placeholder="Search and select country"
                  searchable
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Course Title</label>
                <input
                  type="text"
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={courseTitle}
                  onChange={e => setCourseTitle(e.target.value)}
                  placeholder="e.g., Computer Science"
                  required
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Course Description</label>
                <RichTextEditor
                  value={courseDescription}
                  onChange={setCourseDescription}
                  placeholder="Official course description..."
                  minHeight={160}
                />
              </div>
            </div>
          );
        case 3:
          return (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-[var(--color-text-muted)]">Tell the AI how to improve your SOP. Suggestions from your analysis are included automatically when you pick a document.</p>
              <label className="text-[var(--color-text-secondary)] font-medium">Guide Statement</label>
              <textarea
                className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                value={guideStatement}
                onChange={e => setGuideStatement(e.target.value)}
                rows={8}
                required
              />
            </div>
          );
        case 4: {
          const doc = documents.find(d => d.id === selectedDocId);
          return (
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">Review Your Revamp Request</h3>
              <div className="bg-[var(--color-surface-muted)] rounded-lg p-4 text-[var(--color-text-secondary)] text-sm max-h-96 overflow-y-auto">
                <div><b>Document Type:</b> {docType.toUpperCase()}</div>
                <div><b>Document:</b> {doc ? formatDocumentDisplayName(doc.original_filename || doc.filename, { documentType: doc.document_type, documentId: doc.id }) : ''}</div>
                <div><b>Preferred Full Legal Name:</b> {preferredFullLegalName || 'Not set'}</div>
                <div><b>Program Type:</b> {programType || 'Not set'}</div>
                <div><b>University:</b> {university || 'Not set'}</div>
                <div><b>Country:</b> {country || 'Not set'}</div>
                <div><b>Course Title:</b> {courseTitle || 'Not set'}</div>
                <div><b>Guide Statement:</b> <span className="whitespace-pre-line">{guideStatement}</span></div>
                <div><b>Style/Tone:</b> {style}</div>
              </div>
              <button
                type="submit"
                className="brand-gradient-btn w-full px-6 py-2 rounded-lg disabled:opacity-60 mt-4"
                disabled={loading}
              >
                {loading ? 'Revamping...' : 'Revamp Now'}
              </button>
            </div>
          );
        }
        default:
          return null;
      }
    }

    // Contract: 3-step wizard
    switch (wizardStep) {
        case 0:
          return (
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Document Type</label>
                <select
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={docType}
                  onChange={e => { setDocType(e.target.value); setSelectedDocId(''); setDocTypeTouched(true); setDocSearchInput(''); setWizardStep(0); }}
                  required
                >
                  <option value="cv">CV</option>
                  <option value="sop">SOP</option>
                  {canRevampContract ? <option value="contract">Contract</option> : null}
                </select>
              </div>
              <div className="flex-1 flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Style/Tone (optional)</label>
                <select
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={style}
                  onChange={e => setStyle(e.target.value)}
                >
                  <option value="">Select style/tone</option>
                  {styleOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              {docType === 'contract' && (
                <div className="flex-1 flex flex-col">
                  <label className="text-[var(--color-text-secondary)] font-medium">In Favour Of</label>
                  <select
                    className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                    value={inFavourOf}
                    onChange={e => setInFavourOf(e.target.value)}
                    required
                  >
                    <option value="">Select party</option>
                    {favourOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        case 1:
          return (
            <div className="flex flex-col gap-4">
              <DocumentSearchField
                docSearchInput={docSearchInput}
                setDocSearchInput={setDocSearchInput}
                docDropdownOpen={docDropdownOpen}
                setDocDropdownOpen={setDocDropdownOpen}
                filteredDocs={filteredDocs}
                selectedDocId={selectedDocId}
                setSelectedDocId={setSelectedDocId}
                docType={docType}
              />
              <div className="flex flex-col">
                <label className="text-[var(--color-text-secondary)] font-medium">Guide Statement</label>
                <textarea
                  className="themed-input rounded-lg px-4 py-2 focus:ring-2 focus:ring-[var(--color-ring)]"
                  value={guideStatement}
                  onChange={e => setGuideStatement(e.target.value)}
                  rows={6}
                  required
                />
              </div>
            </div>
          );
        case 2: {
          const doc = documents.find(d => d.id === selectedDocId);
          return (
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">Review Your Revamp Request</h3>
              <div className="bg-[var(--color-surface-muted)] rounded-lg p-4 text-[var(--color-text-secondary)] text-sm max-h-96 overflow-y-auto">
                <div><b>Document Type:</b> {docType.toUpperCase()}</div>
                <div><b>Document:</b> {doc ? formatDocumentDisplayName(doc.original_filename || doc.filename, { documentType: doc.document_type, documentId: doc.id }) : ''}</div>
                {docType === 'contract' && <div><b>In Favour Of:</b> {inFavourOf ? favourOptions.find(opt => opt.value === inFavourOf)?.label : ''}</div>}
                <div><b>Guide Statement:</b> <span className="whitespace-pre-line">{guideStatement}</span></div>
                <div><b>Style/Tone:</b> {style}</div>
              </div>
              <button
                type="submit"
                className="brand-gradient-btn w-full px-6 py-2 rounded-lg disabled:opacity-60 mt-4"
                disabled={loading}
              >
                {loading ? 'Revamping...' : 'Revamp Now'}
              </button>
            </div>
          );
        }
        default:
          return null;
      }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <ProcessingDisclaimerModal
        open={disclaimerOpen}
        onConfirm={confirmProcessing}
        onClose={dismissDisclaimer}
        actionLabel="Revamp Document"
      />
      <div className="w-full">
        <div className={`flex ${window.innerWidth < 768 ? 'flex-col items-start gap-3' : 'items-center justify-between'} mb-6 px-4`}>
          <h1 className="text-3xl font-bold text-[var(--color-text)] break-words mt-4">
            Revamp{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
              Documents
            </span>
          </h1>
          <button
            className={`brand-gradient-btn px-4 py-2 rounded-lg ${window.innerWidth < 768 ? 'min-w-[210px] max-w-[260px]' : 'min-w-[180px] max-w-[220px]'}`}
            onClick={() => setShowModal(true)}
          >
            Revamp New Document
          </button>
        </div>
        
        {/* Subscription Notification */}
        <div className="mb-6 px-4">
          <SubscriptionNotification />
        </div>
        {/* List of revamped documents only */}
        <div className={`elevated-card rounded-lg p-6 mb-6 mx-4 ${window.innerWidth < 768 ? 'p-2' : ''}`}>
          <h2 className="text-xl font-semibold text-[var(--color-text)] mb-4">Your Documents</h2>
          
          {/* Show notification box when no revamped documents exist */}
          {modalDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-24 h-24 bg-[var(--color-surface-muted)] rounded-full flex items-center justify-center mb-4">
                <FileText className="w-12 h-12 text-[var(--color-text-muted)]" />
              </div>
              <h3 className="text-xl font-semibold text-[var(--color-text)] mb-2">No revamped documents found</h3>
              <p className="text-[var(--color-text-muted)] mb-6 max-w-md">
                Start by revamping your first document to see it appear here.
              </p>
              <button
                className="brand-gradient-btn px-6 py-3 rounded-lg"
                onClick={() => setShowModal(true)}
              >
                Revamp Document
              </button>
            </div>
          ) : (
            /* Show existing documents grid when there are revamped documents */
            <div className={`grid ${window.innerWidth < 768 ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-4'}`}>
              {modalDocs.map(doc => (
                <div key={doc.revampId} className={`bg-[var(--color-surface-muted)] rounded-lg p-4 flex flex-col items-start gap-2 border border-[var(--color-border)]`}>
                  <div className="flex items-center gap-3 w-full">
                    <FileText className="w-6 h-6 text-[var(--color-primary)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className={`text-[var(--color-text)] font-medium truncate ${window.innerWidth < 768 ? 'text-base' : 'text-lg'}`} title={doc.original_filename || doc.filename}>
                        {formatDocumentDisplayName(doc.original_filename || doc.filename, {
                          documentType: doc.document_type,
                          documentId: doc.id,
                        })}
                      </div>
                      <div className="text-[var(--color-text-muted)] text-xs mb-1">{doc.document_type} • {new Date(doc.created_at).toLocaleDateString()}</div>
                      <div className="flex items-center gap-3 mt-1">
                        <a
                          href={`/revamp-result/${doc.id}`}
                          className="text-[var(--color-primary)] hover:underline font-medium text-sm"
                        >
                          View Revamp
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteRevamp(doc)}
                          className="text-[var(--color-danger)] hover:underline font-medium text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Modal for new revamp (wizard) */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-overlay)] backdrop-blur-sm animate-fadeIn p-2 sm:p-4">
            <div className="elevated-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden animate-scaleIn">
              <div className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] px-6 sm:px-8 pt-6 sm:pt-8 pb-4 flex flex-col items-center shrink-0">
                <BarChart3 className="w-12 h-12 sm:w-14 sm:h-14 text-white mb-2 drop-shadow-lg" />
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Revamp Document</h2>
                <p className="text-sm text-white/90 mb-2">{wizardSteps[wizardStep]}</p>
                <div className="flex items-center gap-2 mt-1">
                  {wizardSteps.map((step, idx) => (
                    <div
                      key={step}
                      className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${wizardStep === idx ? 'bg-white' : wizardStep > idx ? 'bg-white/60' : 'bg-white/25'} transition-all`}
                      title={step}
                    />
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
                onClick={() => { setShowModal(false); setWizardStep(0); }}
              >
                <X className="w-6 h-6" />
              </button>
              <form className="flex flex-col flex-1 min-h-0 overflow-hidden" onSubmit={handleRevampSubmit}>
                <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 min-h-0">
                  {renderWizardStep()}
                </div>
                <div className="flex items-center justify-between gap-4 px-6 sm:px-8 py-4 border-t border-[var(--color-border)] shrink-0 bg-[var(--color-surface)]">
                  <button
                    type="button"
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)] disabled:opacity-50"
                    onClick={() => setWizardStep(wizardStep - 1)}
                    disabled={wizardStep === 0}
                  >
                    <ChevronLeft className="w-5 h-5" /> Previous
                  </button>
                  {wizardStep < wizardSteps.length - 1 && (
                    <button
                      type="button"
                      className="brand-gradient-btn flex items-center gap-1 px-4 py-2 rounded-lg disabled:opacity-50"
                      onClick={() => setWizardStep(wizardStep + 1)}
                      disabled={!canNext()}
                    >
                      Next <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </form>
            </div>
            <style>{`
              .animate-fadeIn { animation: fadeInModal 0.25s ease; }
              .animate-scaleIn { animation: scaleInModal 0.25s cubic-bezier(.4,2,.6,1); }
              @keyframes fadeInModal { from { opacity: 0; } to { opacity: 1; } }
              @keyframes scaleInModal { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            `}</style>
          </div>
        )}
      </div>
      <ConfirmModal
        open={Boolean(revampToDelete)}
        title="Delete revamp?"
        message={
          revampToDelete
            ? `Delete revamped "${formatDocumentDisplayName(revampToDelete.original_filename || revampToDelete.filename, {
                documentType: revampToDelete.document_type,
                documentId: revampToDelete.id,
              })}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onCancel={() => setRevampToDelete(null)}
        onConfirm={confirmDeleteRevamp}
      />
    </div>
  );
};

export default RevampMenuPage; 